// The report column catalogue: every Laporan column is defined ONCE here
// (what it is called, what kind of value it holds and how wide it should
// be), and the report tables compose their columns from it in
// their own order. Pure module on purpose: no React, no antd, nothing from
// pdf/, so the lazy PDF chunk can read the same widths and labels later.
//
// Labels are i18n KEYS, never translated text. translate() runs where the
// column is built (inside the component body, per render), which keeps a
// language switch live and avoids the "first language sticks for the
// session" bug the PDF layer documents on its SortMeta builders.
import { translate } from "shared/languages";
import { PackageType } from "shared/types/analytics";
import { formatCurrency } from "shared/utils";
import { localTzDate } from "shared/utils/dayjs";

export type ColumnKind =
  | "name"
  | "text"
  | "count"
  | "money"
  | "percent"
  | "duration"
  | "date"
  | "datetime"
  | "daterange"
  | "chip"
  | "code"
  | "twoLine";

export type KindSpec = {
  // Share of the table width in percent. null = the one column that takes
  // whatever is left (the bold first column).
  share: number | null;
  // Pixels counted into the table's minimum width; below the sum of these
  // the table scrolls sideways instead of squashing its columns. CSS px.
  min: number;
};

export const KIND_SPEC: Record<ColumnKind, KindSpec> = {
  name: { share: null, min: 240 },
  text: { share: 14, min: 150 },
  count: { share: 9, min: 90 },
  money: { share: 13, min: 130 },
  percent: { share: 10, min: 100 },
  duration: { share: 13, min: 125 },
  date: { share: 12, min: 120 },
  datetime: { share: 14, min: 150 },
  daterange: { share: 16, min: 170 },
  chip: { share: 11, min: 110 },
  code: { share: 11, min: 120 },
  twoLine: { share: 13, min: 130 },
};

// The name column takes what the sized columns leave, clamped to this band.
// Below the minimum (a table with many sized columns) the sized columns are
// scaled down together; above the maximum (a table with few of them) they
// are scaled up together, so a short table never ends in one very long
// first column on a wide monitor.
export const NAME_MIN_SHARE = 22;
export const NAME_MAX_SHARE = 30;

// Percent of the table each column gets, from the columns' shares in order
// (null = the name column, which takes what is left). Sized columns are
// scaled together so the name column stays inside its band and the row
// adds up to 100. The screen table applies the sized percentages and leaves
// the name column width-less; the PDF uses every entry as a flex.
export const shareLayout = (shares: (number | null)[]): number[] => {
  const sized = shares.filter((s): s is number => s !== null);
  const sum = sized.reduce((total, s) => total + s, 0);
  const hasName = sized.length < shares.length;
  const most = hasName ? 100 - NAME_MIN_SHARE : 100;
  const least = hasName ? 100 - NAME_MAX_SHARE : 100;
  const factor =
    sum <= 0 ? 1 : sum > most ? most / sum : sum < least ? least / sum : 1;
  const scaled = shares.map((s) => (s === null ? null : Math.round(s * factor * 10) / 10));
  const used = scaled.reduce((total: number, s) => total + (s ?? 0), 0);
  const names = scaled.filter((s) => s === null).length;
  const remainder = names > 0 ? Math.max(0, 100 - used) / names : 0;
  return scaled.map((s) => s ?? remainder);
};

export type MetricSpec = { kind: ColumnKind; label: string; info?: string };

const m = (kind: ColumnKind, label: string, info?: string): MetricSpec => ({
  kind,
  label,
  ...(info ? { info } : {}),
});
const c = (key: string) => `analyticsPage.columns.${key}`;

// One entry per metric. The same metric is always the same word on every
// report (house rule: one name per status or label, on every side).
const METRICS = {
  // Bold first columns
  month: m("name", c("month")),
  client: m("name", c("client")),
  class: m("name", c("class")),
  instructor: m("name", c("instructor")),
  code: m("name", c("code")),
  new_client: m("name", c("new_client")),
  package: m("name", c("package")),
  membership: m("name", c("membership")),
  // Secondary text
  instructor_text: m("text", c("instructor")),
  product: m("text", c("product")),
  last_product: m("text", c("last_product")),
  conversion_product: m("text", c("conversion_product")),
  referrer: m("text", c("referrer")),
  referrer_reward: m("text", c("referrer_reward")),
  new_client_reward: m("text", c("new_client_reward")),
  reward: m("text", c("reward")),
  program: m("text", c("program")),
  used_for: m("text", c("used_for")),
  claimed_by: m("text", c("claimed_by")),
  discount_rule: m("text", c("discount")),
  payment_method: m("text", "paymentMethods.col_payment_method"),
  // Codes and chips
  booking_code: m("code", c("booking_code")),
  transaction_id: m("code", "label.transaction_id"),
  class_type: m("chip", c("class_type")),
  status: m("chip", "label.status"),
  booking_status: m("chip", "label.booking_status"),
  // Counts
  sessions_completed: m("count", c("sessions_completed")),
  open_sessions: m("count", c("open_sessions")),
  private_sessions: m("count", c("private_sessions")),
  bookings: m("count", c("bookings")),
  attendees: m("count", c("attendees")),
  no_shows: m("count", c("no_shows")),
  unique_clients: m("count", c("unique_clients")),
  new_clients: m("count", c("new_clients")),
  active: m("count", c("active")),
  sold: m("count", c("sold")),
  upgrades: m("count", c("upgrades"), c("upgrades_info")),
  packages_bought: m("count", c("packages_bought"), c("packages_bought_info")),
  memberships_bought: m("count", c("memberships_bought")),
  remaining_credits: m("count", c("remaining_credits")),
  credits_used: m("count", c("credits_used")),
  // Credits one booking took (booking_v2.credit_cost).
  credit_cost: m("count", "label.credits"),
  expiring_soon: m("count", c("expiring_soon")),
  milestone: m("count", c("milestone")),
  uses: m("count", c("uses")),
  days_inactive: m("count", c("days_inactive")),
  // Rates and durations
  fill_rate: m("percent", c("fill_rate")),
  session_contribution: m("percent", c("session_contribution")),
  repurchase_rate: m("percent", c("repurchase_rate"), c("repurchase_rate_info")),
  avg_days_to_exhaust: m("duration", c("avg_days_to_exhaust"), c("avg_days_to_exhaust_info")),
  avg_visits: m("duration", c("avg_visits"), c("avg_visits_info")),
  // Money. Two words carry the money story: Penjualan is cash from
  // purchases and upgrades; Pendapatan dari Booking is revenue earned as
  // credits are consumed per attended session. Never a bare "Pendapatan".
  sales: m("money", c("sales"), c("sales_info")),
  total_sales: m("money", c("total_sales"), c("sales_info")),
  package_sales: m("money", c("package_sales"), c("sales_info")),
  membership_sales: m("money", c("membership_sales"), c("sales_info")),
  booking_revenue: m("money", c("booking_revenue"), c("booking_revenue_info")),
  // Pembelian's four money columns, the faktur pajak's own row: Harga Jual
  // (the sale's price before discount and before tax), Diskon, Pajak (the
  // tax inside or on top of what was paid), Total (the money received).
  // Pajak carries no tooltip by decision (chris, 2026-10-14): the word is
  // plain; Total's basis is on the card that sums it.
  price_before_tax: m("money", c("price_before_tax"), c("price_before_tax_info")),
  tax: m("money", c("tax")),
  total_paid: m("money", c("total_paid")),
  // The referral conversions table: what the converting transaction brought
  // in, the fee on an upgrade. Shares its definition with the KPI it sums to.
  purchase_value: m("money", c("purchase_value"), "analyticsPage.referral.kpi_first_purchase_revenue_info"),
  // Every discount figure in Laporan is before tax (the Statement's basis);
  // the tooltip rides the metric, so Pembelian, Loyalty and Pemakaian Reward
  // say it the same way.
  discount: m("money", c("discount"), c("discount_info")),
  total_discount: m("money", c("total_discount"), c("total_discount_info")),
  // Dates
  date: m("datetime", c("date")),
  start_time: m("datetime", c("start_time")),
  purchased_at: m("datetime", c("purchased_at")),
  claimed_at: m("datetime", c("claimed_at")),
  converted_at: m("datetime", c("converted_at")),
  last_purchase: m("date", c("last_purchase")),
  last_session: m("date", c("last_session")),
  last_used: m("date", c("last_used")),
  expiry: m("date", c("expiry")),
  purchase_date: m("date", "label.purchase_date"),
  auto_start: m("date", c("auto_start")),
  promo_period: m("daterange", c("promo_period")),
};

export type ReportMetric = keyof typeof METRICS;
export const REPORT_METRICS: Record<ReportMetric, MetricSpec> = METRICS;

// ---- Shared formatters (the ONE copy; the report pages used to carry
// eight identical formatDateOrDash functions) ----

export const DATE_FORMAT = "DD MMM YYYY";
export const DATETIME_FORMAT = "DD MMM YYYY, HH:mm";

// In the active language: "05 Okt 2026" in Indonesian, "05 Oct 2026" in English.
export const formatDateOrDash = (value?: string | null): string =>
  value ? localTzDate(value).format(DATE_FORMAT) : "-";

export const formatDateTime = (value: string): string => localTzDate(value).format(DATETIME_FORMAT);

// A range needs both ends; a promo without dates prints "-", not "- – -".
export const formatDateRangeOrDash = (from?: string | null, to?: string | null): string =>
  from && to ? `${formatDateOrDash(from)} – ${formatDateOrDash(to)}` : "-";

// A null rate or average means "no qualifying cohort yet", not zero.
export const formatPercentOrDash = (value: number | null | undefined): string =>
  value === null || value === undefined ? "-" : `${value.toFixed(1)}%`;

export const formatDaysOrDash = (value: number | null | undefined): string =>
  value === null || value === undefined
    ? "-"
    : `${Math.round(value)} ${translate("class_settings.unit.days")}`;

export const formatCountOrDash = (value: number | null | undefined): string =>
  value === null || value === undefined ? "-" : value.toFixed(1);

export const formatMoneyOrDash = (value: number | null | undefined): string =>
  value === null || value === undefined ? "-" : formatCurrency(value);

// "Paket" or "Membership", resolved at call time so a language switch and
// the report's product type both stay live.
export const productNameLabel = (type: PackageType): string =>
  translate(type === "membership" ? "analyticsPage.columns.membership" : "analyticsPage.columns.package");

// ---- Sorter helpers. Same comparators the pages used by hand: numeric
// subtraction (with the page's own null sentinel) and ISO-string order. ----

export const byNumber =
  <Row>(field: keyof Row, nullAs = 0) =>
  (a: Row, b: Row): number =>
    ((a[field] as unknown as number | null | undefined) ?? nullAs) -
    ((b[field] as unknown as number | null | undefined) ?? nullAs);

export const byIso =
  <Row>(field: keyof Row) =>
  (a: Row, b: Row): number =>
    String(a[field] ?? "").localeCompare(String(b[field] ?? ""));
